import { call } from '@advanza/api'
import { readGenericError } from 'components/keywordMappings/keywordMappingModel'
import { KeywordMappingImport, readKeywordMappingImport } from 'interfaces/KeywordMappingImport'

const PART_UPLOAD_ATTEMPTS = 3
const START_FAILED = 'The import could not be started.'
const FINISH_FAILED = 'The import could not be queued. Try again.'
const PART_FAILED = 'The part did not arrive. Try again.'

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function readPositiveInteger(value: unknown): number | null {
    if (typeof value !== 'number') return null
    if (Number.isInteger(value) === false) return null
    if (value < 1) return null
    return value
}

interface StartedImport {
    importId: number
    partBytes: number
    partCount: number
}

function readStartedImport(value: unknown): StartedImport | null {
    if (!isRecord(value)) return null

    const imported = readKeywordMappingImport(value.import)
    const partBytes = readPositiveInteger(value.partBytes)
    const partCount = readPositiveInteger(value.partCount)
    if (imported === null) return null
    if (partBytes === null) return null
    if (partCount === null) return null

    return {
        importId: imported.keyword_mapping_import_id,
        partBytes,
        partCount,
    }
}

function readFinishedImport(value: unknown): KeywordMappingImport | null {
    if (!isRecord(value)) return null
    return readKeywordMappingImport(value.import)
}

function serverError(response: unknown, fallback: string): Error {
    return new Error(readGenericError(response, fallback))
}

function uploadContinued(imported: KeywordMappingImport): boolean {
    if (imported.status === 'queued') return true
    if (imported.status === 'running') return true
    if (imported.status === 'done') return true
    return false
}

async function uploadPart(importId: number, partIndex: number, part: Blob): Promise<void> {
    for (let attempt = 1; attempt <= PART_UPLOAD_ATTEMPTS; attempt += 1) {
        try {
            await call('office/keyword-mappings/import-part', {
                payload: {
                    importId: String(importId),
                    // FormData drops falsy values, so part 0 has to travel as the string '0'.
                    partIndex: String(partIndex),
                    file: part,
                },
            })
            return
        } catch (response: unknown) {
            const message = readGenericError(response, '')
            if (message !== '' && message !== PART_FAILED) {
                throw new Error(message)
            }
            if (attempt === PART_UPLOAD_ATTEMPTS) {
                throw serverError(response, PART_FAILED)
            }
        }
    }
}

async function finishImport(importId: number): Promise<unknown> {
    let lastResponse: unknown = undefined
    for (let attempt = 1; attempt <= PART_UPLOAD_ATTEMPTS; attempt += 1) {
        try {
            return await call('office/keyword-mappings/import-finish', {
                json: { importId },
            })
        } catch (response: unknown) {
            const message = readGenericError(response, '')
            if (message !== '') {
                throw new Error(message)
            }
            lastResponse = response
        }
    }

    throw serverError(lastResponse, FINISH_FAILED)
}

async function abortImport(importId: number): Promise<KeywordMappingImport | null> {
    try {
        const response: unknown = await call('office/keyword-mappings/import-abort', {
            json: { importId },
        })
        return readFinishedImport(response)
    } catch {
        return null
    }
}

export async function uploadKeywordMappingCsv(
    file: File,
    onProgress: (uploadedParts: number, partCount: number) => void,
): Promise<KeywordMappingImport> {
    let startResponse: unknown
    try {
        startResponse = await call('office/keyword-mappings/import-start', {
            json: { fileName: file.name, fileSize: file.size },
        })
    } catch (response: unknown) {
        throw serverError(response, START_FAILED)
    }

    const started = readStartedImport(startResponse)
    if (started === null) {
        throw new Error(START_FAILED)
    }

    onProgress(0, started.partCount)

    try {
        for (let partIndex = 0; partIndex < started.partCount; partIndex += 1) {
            const startByte = partIndex * started.partBytes
            const part = file.slice(startByte, startByte + started.partBytes)
            await uploadPart(started.importId, partIndex, part)
            onProgress(partIndex + 1, started.partCount)
        }

        const finishResponse = await finishImport(started.importId)
        const finished = readFinishedImport(finishResponse)
        if (finished === null) {
            throw new Error(FINISH_FAILED)
        }

        return finished
    } catch (error: unknown) {
        const aborted = await abortImport(started.importId)
        if (aborted !== null && uploadContinued(aborted)) {
            return aborted
        }
        throw error
    }
}
